import React, { forwardRef, useEffect, useId, useRef } from "react";
import Icon from "./Icon";
import "./Checkbox.scss";

// Fontaine M15 — Checkbox. Visual contract: Style Guide V2 `checkbox-*` (20px box, 6px radius, accent when on).
// A real <input type="checkbox"> drives state and a11y; the box is drawn from tokens.

/**
 * @param {boolean} [checked]
 * @param {boolean} [indeterminate]       bulk-selection "some" state (aria-checked="mixed")
 * @param {(next: boolean, event) => void} [onChange]
 * @param {ReactNode} [label]
 * @param {ReactNode} [description]       helper text under the label
 * @param {string} [error]                inline error, sets aria-invalid
 */
const Checkbox = forwardRef(function Checkbox(
  { checked, defaultChecked, indeterminate = false, onChange, label, description, error, disabled = false, id, className = "", name, value, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id || `ft-check-${autoId}`;
  const inner = useRef(null);
  const setRefs = (el) => {
    inner.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  const classes = ["ft-check", disabled ? "is-disabled" : "", error ? "is-error" : "", className].filter(Boolean).join(" ");

  return (
    <label className={classes} htmlFor={inputId}>
      <input
        ref={setRefs}
        id={inputId}
        type="checkbox"
        className="ft-check__input"
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        name={name}
        value={value}
        aria-checked={indeterminate ? "mixed" : undefined}
        aria-labelledby={label != null ? `${inputId}-label` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={description || error ? `${inputId}-desc` : undefined}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      <span className="ft-check__box" aria-hidden="true">
        <Icon name={indeterminate ? "Minus" : "Check"} size="xs" strokeWidth={3} />
      </span>
      {label != null || description || error ? (
        <span className="ft-check__text">
          {label != null ? <span className="ft-check__label" id={`${inputId}-label`}>{label}</span> : null}
          {description || error ? (
            <span className={`ft-check__desc${error ? " is-error" : ""}`} id={`${inputId}-desc`}>{error || description}</span>
          ) : null}
        </span>
      ) : null}
    </label>
  );
});

export default Checkbox;
